import { Link } from 'react-router'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { formatCount } from '../../format'

/**
 * What still stands between this customer and their first call.
 *
 * A new customer signs in to a dashboard of zeros. Every number is correct and
 * none of them says what to do, and the order matters: an application before a
 * credential, access before either is worth anything, money before a call
 * succeeds. Naming the steps in that order, marking the ones already done, and
 * linking each to the screen that does it turns the same zeros into a route.
 *
 * It disappears once the customer has made a request, because by then they
 * know the way and a permanent checklist is clutter.
 *
 * Two of the five steps are ours, not theirs — access and funding. Each is
 * labelled «از سمت ما» and its button says «پیگیری» rather than «انجام»,
 * because the screen it opens explains the step and shows its result; it does
 * not perform it. A checklist that reads as five things the customer has to do
 * leaves them waiting on two of them without knowing they are waiting.
 */
export interface OnboardingState {
  hasApplication: boolean
  hasAccess: boolean
  hasCredential: boolean
  hasFunds: boolean
  hasRequests: boolean
}

interface Step {
  label: string
  done: boolean
  hint: string
  /** Who performs it. An operator step is ours to do and theirs to chase. */
  owner: 'customer' | 'operator'
  to?: string
  action?: string
}

export function GettingStarted({ state }: { state: OnboardingState }) {
  if (state.hasRequests) return null

  const steps: Step[] = [
    {
      label: 'ساخت اپلیکیشن',
      owner: 'customer',
      done: state.hasApplication,
      hint: 'هر اپلیکیشن، اعتبارنامه‌ها و دسترسی‌های خودش را دارد.',
      to: '/applications',
      action: 'اپلیکیشن‌ها',
    },
    {
      label: 'دریافت دسترسی سرویس',
      owner: 'operator',
      done: state.hasAccess,
      hint: 'دسترسی را ما فعال می‌کنیم؛ اگر چیزی لازم دارید با ما تماس بگیرید.',
      to: '/services',
      action: 'پیگیری دسترسی‌ها',
    },
    {
      label: 'ساخت اعتبارنامه',
      owner: 'customer',
      done: state.hasCredential,
      hint: 'رمز اعتبارنامه فقط یک‌بار نشان داده می‌شود؛ همان‌جا ذخیره‌اش کنید.',
      to: '/credentials',
      action: 'اعتبارنامه‌ها',
    },
    {
      label: 'شارژ کیف پول',
      owner: 'operator',
      done: state.hasFunds,
      hint: 'هر فراخوانی موفق از کیف پول برداشت می‌شود؛ بدون موجودی، درخواست رد می‌شود.',
      to: '/wallet',
      action: 'پیگیری کیف پول',
    },
    {
      label: 'اولین فراخوانی',
      owner: 'customer',
      done: state.hasRequests,
      hint: 'می‌توانید همان‌جا در صفحهٔ سرویس، یک فراخوانی آزمایشی انجام دهید.',
      to: '/services',
      action: 'اجرای آزمایشی',
    },
  ]

  const remaining = steps.filter((step) => !step.done).length
  const waitingOnUs = steps.filter((step) => !step.done && step.owner === 'operator').length

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h2" gutterBottom>
        شروع کار
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {remaining === 0
          ? 'همه‌چیز آماده است؛ می‌توانید اولین فراخوانی را انجام دهید.'
          : `برای انجام اولین فراخوانی، ${formatCount(remaining)} قدم باقی مانده است.`}
      </Typography>

      <Stack spacing={1.5}>
        {steps.map((step) => (
          <Stack
            key={step.label}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ alignItems: { sm: 'center' } }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 260 }}>
              {step.done ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
              )}
              <Typography
                variant="body2"
                sx={{ fontWeight: step.done ? 400 : 600 }}
                color={step.done ? 'text.secondary' : 'text.primary'}
              >
                {step.label}
              </Typography>
              {step.owner === 'operator' ? (
                <Chip label="از سمت ما" size="small" variant="outlined" />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              {step.hint}
            </Typography>
            {!step.done && step.to ? (
              <Box sx={{ flexShrink: 0 }}>
                <Button component={Link} to={step.to} size="small" variant="outlined">
                  {step.action}
                </Button>
              </Box>
            ) : null}
          </Stack>
        ))}
      </Stack>

      {waitingOnUs > 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          قدم‌های نشان‌دار «از سمت ما» را ما انجام می‌دهیم: فعال‌کردن دسترسی سرویس‌ها و ثبت شارژ
          کیف پول. از سمت شما کاری لازم نیست؛ اگر منتظر مانده‌اید با ما تماس بگیرید.
        </Alert>
      ) : null}
    </Paper>
  )
}
